import type { NodoTerritorio } from "./api";

/**
 * El camino de cada nodo desde la raíz: «Nuevo León › Guadalupe › Distrito
 * local 13».
 *
 * POR QUÉ EL CAMINO Y NO EL NOMBRE: los distritos locales se llaman igual en
 * municipios distintos («Distrito local 13»), y las secciones son solo un
 * número. Una lista para asignar territorio con nombres repetidos es una
 * lista en la que alguien elige el equivocado y nadie lo nota hasta que un
 * coordinador ve el padrón de otro lado.
 *
 * EL CORTE POR CICLO no es paranoia decorativa: `territorio.nodo.padre_id`
 * apunta a la misma tabla y la base NO impide que alguien, con un UPDATE,
 * cierre un lazo. Sin la guarda, esta función se llama a sí misma hasta
 * colgar la pestaña — y el síntoma («la página se congela al abrir usuarios»)
 * no se parece en nada a la causa.
 */
export function caminos(nodos: NodoTerritorio[]): Map<number, string> {
  const porId = new Map(nodos.map((n) => [n.id, n]));
  const cache = new Map<number, string>();

  const nombreDe = (n: NodoTerritorio) =>
    n.nombre?.trim() ? n.nombre : `${n.tipo} ${n.clave}`;

  function camino(n: NodoTerritorio, vistos: Set<number>): string {
    const ya = cache.get(n.id);
    if (ya !== undefined) return ya;
    if (vistos.has(n.id)) return `${nombreDe(n)} (ciclo)`;
    vistos.add(n.id);
    const padre = n.padre_id != null ? porId.get(n.padre_id) : undefined;
    const texto = padre ? `${camino(padre, vistos)} › ${nombreDe(n)}` : nombreDe(n);
    // Solo se memoriza lo que se calculó SIN tropezar con un ciclo: guardar
    // un «(ciclo)» en la caché se lo pegaría a los hermanos sanos.
    if (!texto.includes("(ciclo)")) cache.set(n.id, texto);
    return texto;
  }

  const salida = new Map<number, string>();
  for (const n of nodos) salida.set(n.id, camino(n, new Set()));
  return salida;
}

/** Los nodos a los que tiene sentido colgar a una persona. Una manzana es un
 *  ámbito legítimo para el esquema y una lista de 96 mil opciones para quien
 *  asigna: no se ofrece. */
export function asignables(nodos: NodoTerritorio[]): NodoTerritorio[] {
  return nodos.filter((n) => n.tipo !== "manzana");
}
